import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import Button from './Button';

const CookieContainer = styled(motion.div)`
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  right: 2rem;
  background-color: rgba(18, 18, 18, 0.95);
  padding: 1.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 500px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    padding: 1.2rem 1.5rem;
  }
`;

const CookieTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const CookieText = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const CookieActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CookieLink = styled(Link)`
  color: var(--accent-primary);
  text-decoration: underline;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--accent-secondary);
  }
`;

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const { language } = useLanguage();
  
  useEffect(() => {
    // Controlla se l'utente ha già dato il consenso
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Mostra il banner dopo un breve ritardo
      const timer = setTimeout(() => {
        setVisible(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);
  
  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setVisible(false);
  };
  
  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setVisible(false);
  };
  
  const title = language === 'it' ? 'Informativa sui Cookie' : 'Cookie Policy';
  const text = language === 'it' 
    ? 'Utilizziamo i cookie per migliorare la tua esperienza sul nostro sito. Leggi la nostra ' 
    : 'We use cookies to enhance your experience on our site. Read our ';
  const policyText = language === 'it' ? 'Informativa sui Cookie' : 'Cookie Policy';
  const acceptText = language === 'it' ? 'Accetta' : 'Accept';
  const declineText = language === 'it' ? 'Rifiuta' : 'Decline';
  
  return (
    <AnimatePresence>
      {visible && (
        <CookieContainer
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <CookieTitle>{title}</CookieTitle>
          <CookieText>
            {text}
            <CookieLink to="/cookie-policy">{policyText}</CookieLink>
            {language === 'it' ? ' per saperne di più.' : ' for more information.'}
          </CookieText>
          <CookieActions>
            <Button variant="outline" onClick={handleDecline}>
              {declineText}
            </Button>
            <Button variant="primary" onClick={handleAccept}>
              {acceptText}
            </Button>
          </CookieActions>
        </CookieContainer>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent; 